import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import api from "../../services/api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Trash2 } from "lucide-react";
import Select from "../../components/form/Select";

interface ProductOption {
    id: string;
    name: string;
    offers?: { id: string; name: string }[];
}

interface Assignment {
    productId: string;
    offerId: string;
}

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "link"],
        ["clean"],
    ],
};

const quillFormats = [
    "header",
    "bold", "italic", "underline",
    "list",
    "blockquote", "link",
];

export default function NewTerm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ title: "", content: "" });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState<"success" | "error">("success");

    // Assignments
    const [assignmentType, setAssignmentType] = useState<string>("global");
    const [assignments, setAssignments] = useState<Assignment[]>([{ productId: "", offerId: "" }]);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);

    // Buscar produtos (usando /api/products que retorna id como UUID)
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setProductsLoading(true);
                const response = await api.get("/api/products");
                if (response.data && response.data.products) {
                    const prods = response.data.products.map((p: { id: string; name: string }) => ({
                        id: p.id,
                        name: p.name,
                        offers: [] as { id: string; name: string }[]
                    }));
                    setProducts(prods);
                }
            } catch (err: unknown) {
                console.error("Erro ao buscar produtos:", err);
            } finally {
                setProductsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const displayNotification = (message: string, type: "success" | "error") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 5000);
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};
        if (!formData.title.trim()) errors.title = "Titulo e obrigatorio";
        const strippedContent = formData.content.replace(/<(.|\n)*?>/g, "").trim();
        if (!strippedContent) errors.content = "Conteudo e obrigatorio";

        if (assignmentType === "specific") {
            const hasValid = assignments.some(a => a.productId);
            if (!hasValid) errors.assignments = "Selecione pelo menos um produto";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildAssignments = () => {
        if (assignmentType === "global") {
            return [{}];
        }
        return assignments
            .filter(a => a.productId)
            .map(a => {
                const item: { productId: string; offerId?: string } = { productId: a.productId };
                if (a.offerId) item.offerId = a.offerId;
                return item;
            });
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const response = await api.post(`/api/company-terms`, {
                title: formData.title,
                content: formData.content,
                assignments: buildAssignments()
            });

            if (response.data && response.data.success) {
                displayNotification("Termos de uso criados com sucesso!", "success");
                setTimeout(() => {
                    navigate("/products?tab=terms");
                }, 1000);
            }
        } catch (err: unknown) {
            console.error("Erro ao criar termos:", err);
            const axiosErr = err as { response?: { data?: { message?: string } } };
            const message = axiosErr.response?.data?.message || "Erro ao criar termos de uso.";
            displayNotification(message, "error");
        } finally {
            setLoading(false);
        }
    };

    // Gerenciar assignments
    const addAssignment = () => {
        setAssignments([...assignments, { productId: "", offerId: "" }]);
    };

    const removeAssignment = (index: number) => {
        setAssignments(assignments.filter((_, i) => i !== index));
    };

    const updateAssignment = (index: number, field: "productId" | "offerId", value: string) => {
        const updated = [...assignments];
        updated[index] = { ...updated[index], [field]: value };
        if (field === "productId") {
            updated[index].offerId = "";
            // Buscar ofertas do produto selecionado (sob demanda)
            if (value) {
                fetchOffersForProduct(value);
            }
        }
        setAssignments(updated);
    };

    const fetchOffersForProduct = async (productId: string) => {
        // Se ja temos ofertas para este produto, nao buscar de novo
        const existing = products.find(p => p.id === productId);
        if (existing && existing.offers && existing.offers.length > 0) return;

        try {
            const response = await api.get(`/api/products/${productId}/offers-with-links`);
            if (response.data && response.data.offers) {
                const offers = response.data.offers.map((o: { id: string; name: string }) => ({
                    id: o.id,
                    name: o.name
                }));
                setProducts(prev => prev.map(p =>
                    p.id === productId ? { ...p, offers } : p
                ));
            }
        } catch (err: unknown) {
            console.error("Erro ao buscar ofertas:", err);
        }
    };

    const getOffersForProduct = (productId: string) => {
        const product = products.find(p => p.id === productId);
        return product?.offers || [];
    };

    return (
        <div>
            <PageMeta
                title="Novo Termo de Uso | Sellyo"
                description="Criar novo termo de uso"
            />
            <PageBreadcrumb
                pageTitle="Novo Termo de Uso"
                showBackButton={true}
                onBack={() => navigate("/products?tab=terms")}
            />

            <div className="space-y-6">
                <ComponentCard title="Criar Termo de Uso">
                    <div className="space-y-6">
                        <div>
                            <Label htmlFor="term-title">Titulo *</Label>
                            <Input
                                id="term-title"
                                placeholder="Ex: Termos de Uso - Meu Produto"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                error={!!formErrors.title}
                                hint={formErrors.title}
                            />
                        </div>

                        {/* Associacoes */}
                        <div>
                            <Label>Exigir no checkout de *</Label>
                            <div className="flex gap-4 mt-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="assignmentType"
                                        value="global"
                                        checked={assignmentType === "global"}
                                        onChange={() => setAssignmentType("global")}
                                        className="h-4 w-4 text-brand-500 border-gray-300 focus:ring-brand-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Todos os produtos</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="assignmentType"
                                        value="specific"
                                        checked={assignmentType === "specific"}
                                        onChange={() => setAssignmentType("specific")}
                                        className="h-4 w-4 text-brand-500 border-gray-300 focus:ring-brand-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Produtos especificos</span>
                                </label>
                            </div>

                            {assignmentType === "global" && (
                                <p className="mt-2 text-xs text-blue-500 dark:text-blue-400">
                                    Este termo de uso sera exibido em todos os checkouts de todos os produtos, incluindo os futuros.
                                </p>
                            )}

                            {assignmentType === "specific" && (
                                <div className="space-y-3 mt-4">
                                    {productsLoading ? (
                                        <div className="flex justify-center py-4">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                        </div>
                                    ) : (
                                        <>
                                            {assignments.map((assignment, index) => {
                                                const offers = getOffersForProduct(assignment.productId);
                                                const productOptions = products.map(p => ({ value: p.id, label: p.name }));
                                                const offerOptions = [
                                                    { value: "", label: "Todas as ofertas" },
                                                    ...offers.map(o => ({ value: o.id, label: o.name }))
                                                ];

                                                return (
                                                    <div key={index} className="flex gap-3 items-start">
                                                        <div className="flex-1">
                                                            <Select
                                                                options={productOptions}
                                                                placeholder="Selecione um produto"
                                                                value={assignment.productId}
                                                                onChange={(value) => updateAssignment(index, "productId", value)}
                                                            />
                                                        </div>
                                                        {assignment.productId && offers.length > 0 && (
                                                            <div className="flex-1">
                                                                <Select
                                                                    options={offerOptions}
                                                                    placeholder="Todas as ofertas"
                                                                    value={assignment.offerId}
                                                                    onChange={(value) => updateAssignment(index, "offerId", value)}
                                                                />
                                                            </div>
                                                        )}
                                                        {assignments.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAssignment(index)}
                                                                className="mt-1.5 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 className="size-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            <button
                                                type="button"
                                                onClick={addAssignment}
                                                className="flex items-center gap-2 text-sm text-brand-500 hover:text-brand-600 font-medium"
                                            >
                                                <Plus className="size-4" />
                                                Adicionar outro produto
                                            </button>

                                            {formErrors.assignments && (
                                                <p className="text-xs text-error-500">{formErrors.assignments}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <Label>Conteudo *</Label>
                            <div className="quill-terms-editor">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.content}
                                    onChange={(value) => setFormData({ ...formData, content: value })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Escreva os termos de uso aqui..."
                                />
                            </div>
                            {formErrors.content && (
                                <p className="mt-1.5 text-xs text-error-500">{formErrors.content}</p>
                            )}
                        </div>
                    </div>
                </ComponentCard>

                {/* Botoes */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        size="md"
                        onClick={() => navigate("/products?tab=terms")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Criando..." : "Criar Termo"}
                    </Button>
                </div>
            </div>

            {/* Notificacao */}
            {showNotification && (
                <div className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-md transition-all duration-300 ${notificationType === "success"
                    ? "bg-success-500 dark:bg-success-400 text-white"
                    : "bg-danger-500 dark:bg-danger-400 text-white"
                    }`}>
                    <div className="flex items-center">
                        {notificationType === "success" ? (
                            <svg className="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                        <span>{notificationMessage}</span>
                    </div>
                </div>
            )}

            {/* Estilo para aumentar a area do editor */}
            <style>{`
                .quill-terms-editor .ql-container {
                    min-height: 350px;
                    font-size: 14px;
                }
                .quill-terms-editor .ql-editor {
                    min-height: 350px;
                }
                .quill-terms-editor .ql-editor.ql-blank::before {
                    font-style: italic;
                    color: #9ca3af;
                }
            `}</style>
        </div>
    );
}
